import { default as React, useState } from 'react';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
} from '@backstage/plugin-catalog';
import {
  CatalogFilterLayout,
  EntityListPagination,
  EntityListProvider,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import {
  Content,
  ContentHeader,
  PageWithHeader,
} from '@backstage/core-components';
import {
  configApiRef,
  useApi,
  attachComponentData,
} from '@backstage/core-plugin-api';
import { catalogPlugin } from '@backstage/plugin-catalog';

import {
  EntityKindPicker,
  EntityTypePicker,
  UserListPicker,
  EntityOwnerPicker,
  EntityNamespacePicker,
} from '@backstage/plugin-catalog-react';

import { CatalogCardGrid } from '../CatalogCardGrid';
import { CatalogViewToggle, ViewMode } from '../CatalogViewToggle';

export type CatalogPageProps = {
  title: string;
  initialFilter: string;
  allowedKinds: string[];
  pagination?: EntityListPagination;
  columns?: CatalogTableColumnsFunc;
  filters?: React.ReactNode;
  defaultViewMode?: ViewMode;
};

// Create a separate component for the catalog content that uses the hook
const CatalogContent = ({
  title,
  viewMode,
  columns,
}: {
  title: string;
  viewMode: ViewMode;
  columns?: CatalogTableColumnsFunc;
}) => {
  const { totalItems } = useEntityList();
  if (totalItems === undefined) return null;

  if (viewMode === 'cards') {
    return <CatalogCardGrid title={`${title} (${totalItems})`} />;
  }

  return columns ? (
    <CatalogTable title={`${title} (${totalItems})`} columns={columns} />
  ) : (
    <CatalogTable title={`${title} (${totalItems})`} />
  );
};

const CustomCatalogIndexPageComponent = ({
  title,
  initialFilter,
  allowedKinds,
  pagination,
  columns,
  filters,
  defaultViewMode = 'cards',
}: CatalogPageProps) => {
  const configApi = useApi(configApiRef);
  const orgName = configApi.getOptionalString('organization.name') ?? 'Wellsky';
  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);

  const pageTitle = `${orgName} ${title}`;

  return (
    <PageWithHeader title={pageTitle} themeId="home">
      <Content>
        <ContentHeader title="">
          <CatalogViewToggle viewMode={viewMode} onChange={setViewMode} />
        </ContentHeader>
        <EntityListProvider pagination={pagination}>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              {filters || (
                <>
                  <EntityKindPicker
                    initialFilter={initialFilter}
                    allowedKinds={allowedKinds}
                  />
                  <EntityTypePicker />
                  <UserListPicker />
                  <EntityOwnerPicker />
                  <EntityNamespacePicker />
                </>
              )}
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <CatalogContent
                title={title}
                viewMode={viewMode}
                columns={columns}
              />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </PageWithHeader>
  );
};

// Register the catalog plugin's route ref so EntityLayout can resolve it
attachComponentData(
  CustomCatalogIndexPageComponent,
  'core.mountPoint',
  catalogPlugin.routes.catalogIndex,
);

export const CustomCatalogIndexPage = CustomCatalogIndexPageComponent;
